import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  TextField,
  Snackbar,
  Alert,
  Typography,
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  IconButton,
} from "@mui/material";
import { AppDispatch, RootState } from "../redux/store";
import useDetectSession from "../hooks/useDetectSession.hook";
import {
  inputStyle,
  pageHeading,
  primaryBtnStyle,
  primaryBtnStyleOutline,
} from "../styles/styling";
import EditIcon from "@mui/icons-material/Edit";
import AppContainer from "../components/AppContainer";
import { PermissionsByDocKey } from "../interfaces/permissions-interface";
import { transformArrToObj } from "../utils/transform-permissions-obj";
import { useNavigate } from "react-router-dom";
import { getIdleTimeData, updateIdleTime } from "../redux/slices/idleTimeSlice";
import LoadingOverlay from "../components/LoadingOverlay";

const IdleTimePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  useDetectSession();
  const navigate = useNavigate();

  const { idleTime, status, error } = useSelector(
    (state: RootState) => state.idleTime
  );

  const currentUser = useSelector((state: RootState) => state.auth.currentUser);

  const [currentUserPermissions, setCurrentUserPermissions] =
    useState<PermissionsByDocKey>();

  const [edit, setEdit] = useState<boolean>(false);
  const [updateTime, setUpdateTime] = useState<number>(3);

  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  useEffect(() => {
    dispatch(getIdleTimeData())
      .unwrap()
      .then(() => setUpdateTime(idleTime.minutes));
  }, [dispatch, idleTime.minutes]);

  useEffect(() => {
    if (!currentUser) {
      navigate("/login");
    }
    if (currentUser && currentUser.userPermissions) {
      setCurrentUserPermissions(transformArrToObj(currentUser.userPermissions));
    }
  }, [currentUser, navigate]);

  if (status === "failed") {
    return (
      <AppContainer>
        <Typography variant="h6" color="error">
          Error: {error}
        </Typography>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <Box display="flex" alignItems="center" marginBottom={2}>
        <Typography variant="h4" sx={pageHeading}>
          Idle Time
        </Typography>
      </Box>

      <LoadingOverlay loading={status === "loading"} />

      <Paper sx={{ width: "100%" }}>
        <TableContainer>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                <TableCell colSpan={2}>App Idle Time (in minutes)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  {!edit ? (
                    idleTime.minutes
                  ) : (
                    <TextField
                      sx={inputStyle()}
                      type="number"
                      value={updateTime}
                      onChange={(e) => {
                        setUpdateTime(Number(e.target.value));
                      }}
                    />
                  )}
                </TableCell>
                {currentUserPermissions?.mobileAppIdleTime?.permissions?.edit &&
                  !edit && (
                    <TableCell sx={{ width: "60px", alignSelf: "flex-end" }}>
                      <Tooltip title="Edit Idle Time">
                        <IconButton onClick={() => setEdit(true)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                {currentUserPermissions?.mobileAppIdleTime?.permissions?.edit &&
                  edit && (
                    <TableCell
                      sx={{
                        width: "240px",
                        height: "85px",
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderStyle: "none",
                        marginLeft: "auto",
                      }}
                    >
                      <Button
                        sx={primaryBtnStyle}
                        onClick={() => {
                          dispatch(
                            updateIdleTime({
                              idleTime: updateTime,
                              updatedBy: currentUser?.fullname ?? "",
                            })
                          )
                            .unwrap()
                            .then(() => {
                              localStorage.setItem(
                                "sessionTimeout",
                                updateTime.toString()
                              );
                              window.dispatchEvent(new Event("storage"));

                              dispatch(getIdleTimeData());

                              setEdit(false);
                              setSnackbarMessage(
                                "Successfully updated idle time duration."
                              );
                              setSnackbarSeverity("success");
                            })
                            .catch(() => {
                              setSnackbarMessage(
                                "Failed to update idle time duration."
                              );
                              setSnackbarSeverity("error");
                            });
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        variant="outlined"
                        sx={primaryBtnStyleOutline}
                        onClick={() => setEdit(false)}
                      >
                        Cancel
                      </Button>
                    </TableCell>
                  )}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </AppContainer>
  );
};

export default IdleTimePage;
